import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Download } from 'lucide-react';
import { productsApi } from '../api/client';
import { useT, useLanguage, localise } from '../lib/i18n';
import { formatCurrency } from '../lib/currency';
import { usePricingContext, exclVat } from '../lib/PricingContext';
import { useAdminPreview } from '../lib/AdminPreviewContext';
import AdminPreviewToggle from '../components/AdminPreviewToggle';
import { useAuth } from '../lib/AuthContext';
import { useCart } from '../hooks/useCart';
import SafeHtml from '../components/common/SafeHtml';

export default function ProductDetail() {
  const { slug } = useParams();
  const t = useT();
  const { locale } = useLanguage();
  const { isB2B } = usePricingContext();
  const { user } = useAuth();
  const { previewMode } = useAdminPreview();
  const isAdmin = user?.role === 'admin';
  const effectiveIsB2B = isAdmin ? previewMode === 'b2b' : isB2B;
  const { addItem } = useCart();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [activeTab, setActiveTab] = useState(null);
  const [addedToCart, setAddedToCart] = useState(false);

  useEffect(() => {
    productsApi.get(slug)
      .then(r => {
        setProduct(r.data);
        // Set first tab with content
        const sections = getRichSections(r.data, locale, t);
        if (sections.length > 0) setActiveTab(sections[0].key);
      })
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (loading) return (
    <div className="max-w-7xl mx-auto px-6 py-24 text-center">
      <div className="w-8 h-8 border-4 border-muted border-t-primary rounded-full animate-spin mx-auto" />
    </div>
  );

  if (!product) return (
    <div className="max-w-7xl mx-auto px-6 py-24 text-center">
      <p className="font-serif text-2xl text-muted-foreground">Product niet gevonden</p>
      <Link to="/catalog" className="text-sm text-primary mt-4 inline-block font-medium">{t.common.back}</Link>
    </div>
  );

  const name = localise(product, 'name', locale);
  const description = localise(product, 'description', locale);
  const allImages = [product.heroImageUrl || product.imageUrl, ...(product.images || [])].filter(Boolean);
  const richSections = getRichSections(product, locale, t);

  return (
    <div data-testid="product-detail-page" className="max-w-7xl mx-auto px-6 md:px-12 py-10 md:py-16">
      <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
        <Link to="/catalog" data-testid="back-to-catalog" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors font-medium">
          <ArrowLeft className="w-4 h-4" /> {t.common.back}
        </Link>
        {isAdmin && <AdminPreviewToggle />}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
        {/* Images */}
        <div className="lg:sticky lg:top-24 lg:self-start">
          <div className="aspect-square rounded-2xl overflow-hidden bg-muted mb-4 shadow-sm">
            {allImages[activeImg] ? (
              <img src={allImages[activeImg]} alt={name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <span className="font-serif text-8xl text-muted-foreground/15">H</span>
              </div>
            )}
          </div>
          {allImages.length > 1 && (
            <div className="flex gap-2.5 flex-wrap">
              {allImages.map((img, i) => (
                <button
                  key={img}
                  onClick={() => setActiveImg(i)}
                  className={`w-16 h-16 rounded-xl overflow-hidden border-2 transition-all duration-200 ${activeImg === i ? 'border-primary shadow-sm' : 'border-transparent hover:border-border'}`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          <h1 data-testid="product-name" className="font-serif text-4xl md:text-5xl font-light leading-tight text-foreground mb-2">{name}</h1>
          {product.botanicalName && (
            <p className="text-sm italic text-muted-foreground mb-6">{product.botanicalName}</p>
          )}

          <div data-testid="product-price" className="flex items-baseline gap-3 mb-8 flex-wrap">
            {effectiveIsB2B ? (
              <>
                <span className="text-3xl font-semibold text-foreground">{formatCurrency(exclVat(product.price, product.vatRate ?? 0.06), locale)}</span>
                <span className="text-sm font-medium text-muted-foreground bg-muted px-2 py-0.5 rounded">excl. BTW</span>
                <span className="text-base text-muted-foreground">({formatCurrency(product.price, locale)} incl.)</span>
              </>
            ) : (
              <>
                <span className="text-3xl font-semibold text-foreground">{formatCurrency(product.price, locale)}</span>
                {product.compareAtPrice && product.compareAtPrice > product.price && (
                  <span className="text-lg text-muted-foreground line-through">{formatCurrency(product.compareAtPrice, locale)}</span>
                )}
              </>
            )}
          </div>

          {description && (
            <p className="text-muted-foreground leading-relaxed mb-8">{description}</p>
          )}

          <div className="mb-8">
            <button
              data-testid="add-to-cart-btn"
              onClick={async () => {
                await addItem(product);
                setAddedToCart(true);
                setTimeout(() => setAddedToCart(false), 2500);
              }}
              disabled={!product.stock || product.stock < 1}
              className="w-full sm:w-auto bg-primary text-primary-foreground rounded-full px-10 py-3.5 font-semibold hover:bg-primary/90 transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed shadow-sm"
            >
              {!product.stock || product.stock < 1 ? t.product.outOfStock : addedToCart ? 'Toegevoegd!' : t.product.addToCart}
            </button>
            {addedToCart && (
              <Link to="/cart" className="ml-3 text-sm text-primary font-medium hover:underline">Winkelmand bekijken</Link>
            )}
          </div>

          {effectiveIsB2B && product.compendiumUrl && (
            <div data-testid="compendium-section" className="mb-8">
              <a
                href={product.compendiumUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="compendium-download"
                className="inline-flex items-center gap-2.5 px-6 py-3 rounded-full border-2 border-primary text-primary text-sm font-semibold hover:bg-primary hover:text-primary-foreground transition-all duration-200"
              >
                <Download className="w-4 h-4" />
                Download compendium
              </a>
            </div>
          )}

          {/* Meta */}
          <div className="border-t border-border pt-6 space-y-3">
            {product.netContent && (
              <div className="flex gap-3 text-sm">
                <span className="text-muted-foreground w-32 shrink-0 font-medium">Inhoud</span>
                <span className="text-foreground">{product.netContent}</span>
              </div>
            )}
            {product.productForm && (
              <div className="flex gap-3 text-sm">
                <span className="text-muted-foreground w-32 shrink-0 font-medium">Vorm</span>
                <span className="text-foreground">{product.productForm}</span>
              </div>
            )}
            {product.origin && (
              <div className="flex gap-3 text-sm">
                <span className="text-muted-foreground w-32 shrink-0 font-medium">{t.product.origin}</span>
                <span className="text-foreground">{product.origin}</span>
              </div>
            )}
            {product.sku && (
              <div className="flex gap-3 text-sm">
                <span className="text-muted-foreground w-32 shrink-0 font-medium">{t.product.sku}</span>
                <span className="font-mono text-foreground">{product.sku}</span>
              </div>
            )}
            {effectiveIsB2B && product.nutNumber && (
              <div data-testid="nut-number" className="flex gap-3 text-sm">
                <span className="text-muted-foreground w-32 shrink-0 font-medium">NUT-nummer</span>
                <span className="font-mono text-foreground">{product.nutNumber}</span>
              </div>
            )}
          </div>

          {/* Rich text tabs */}
          {richSections.length > 0 && (
            <div className="mt-10 border-t border-border pt-8">
              <div className="flex gap-1 mb-6 border-b border-border">
                {richSections.map(s => (
                  <button
                    key={s.key}
                    data-testid={`tab-${s.key}`}
                    onClick={() => setActiveTab(s.key)}
                    className={`px-4 py-2.5 text-sm font-medium transition-all duration-200 border-b-2 -mb-px ${
                      activeTab === s.key
                        ? 'border-primary text-primary'
                        : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
              {richSections.map(s => (
                activeTab === s.key && (
                  <SafeHtml
                    key={s.key}
                    data-testid={`tab-content-${s.key}`}
                    className="text-sm text-muted-foreground leading-relaxed prose prose-sm max-w-none animate-fade-in"
                    html={s.content}
                  />
                )
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function getRichSections(product, locale, t) {
  return [
    { key: 'usage', content: localise(product, 'usageHtml', locale), label: t.product.usage },
    { key: 'indications', content: localise(product, 'indicationsHtml', locale), label: t.product.indications },
    { key: 'composition', content: localise(product, 'compositionHtml', locale), label: t.product.composition },
    { key: 'warnings', content: localise(product, 'warningsHtml', locale), label: t.product.warnings },
  ].filter(s => s.content);
}
